import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import { useTrackingBus, type TrackingBus, type BusUpsertPayload } from "hooks/useTrackingBus";
import { showToast, toastTypes } from "utils";

export default function TrackingBusPage() {
  const toast = useToast();
  const { buses, isLoading, error, create, update, remove } = useTrackingBus();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const cancelRef = useRef<any>(null);

  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<TrackingBus | null>(null);
  const [formValues, setFormValues] = useState<BusUpsertPayload>({ name: "", plate_number: "", driver_id: null, is_active: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<TrackingBus | null>(null);
  const [searchValue, setSearchValue] = useState("");

  const resetForm = useCallback(() => {
    setFormValues({ name: "", plate_number: "", driver_id: null, is_active: true });
    setEditing(null);
    setFormMode("create");
  }, []);

  const openCreate = useCallback(() => { resetForm(); setFormMode("create"); onOpen(); }, [onOpen, resetForm]);

  const openEdit = useCallback((row: TrackingBus) => {
    setEditing(row);
    setFormMode("edit");
    setFormValues({ name: row.name, plate_number: row.plate_number, driver_id: row.driver_id ?? null, is_active: row.is_active });
    onOpen();
  }, [onOpen]);

  const handleSubmit = useCallback(async () => {
    if (!formValues.name.trim() || !formValues.plate_number.trim()) {
      showToast({ toast, message: "Nom et immatriculation obligatoires.", status: toastTypes.error });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: BusUpsertPayload = { name: formValues.name.trim(), plate_number: formValues.plate_number.trim(), driver_id: formValues.driver_id ?? undefined, is_active: formValues.is_active };
      if (formMode === "create") {
        await create(payload);
        showToast({ toast, message: "Bus créé.", status: toastTypes.success });
      } else if (editing) {
        await update(editing.id, payload);
        showToast({ toast, message: "Bus mis à jour.", status: toastTypes.success });
      }
      onClose();
      resetForm();
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || e?.message || "Erreur.", status: toastTypes.error });
    } finally {
      setIsSubmitting(false);
    }
  }, [create, update, editing, formMode, formValues, onClose, resetForm, toast]);

  const handleDeleteClick = useCallback((row: TrackingBus) => { setActiveItem(row); onConfirmOpen(); }, [onConfirmOpen]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await remove(activeItem.id);
      showToast({ toast, message: "Bus supprimé.", status: toastTypes.success });
      onConfirmClose();
      setActiveItem(null);
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error });
    } finally {
      setDeletingId(null);
    }
  }, [activeItem, remove, toast, onConfirmClose]);

  const filteredData = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return buses;
    return buses.filter((b) => b.name.toLowerCase().includes(q) || b.plate_number.toLowerCase().includes(q));
  }, [buses, searchValue]);

  const columns = useMemo(() => {
    const col = createColumnHelper<TrackingBus>();
    return [
      col.accessor("name", { header: "Nom", cell: (info) => <Text>{info.getValue()}</Text> }),
      col.accessor("plate_number", { header: "Immatriculation", cell: (info) => <Text fontWeight="medium">{info.getValue()}</Text> }),
      col.accessor("driver_id", { header: "Conducteur (ID)", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("is_active", { header: "Statut", cell: (info) => <Badge colorScheme={info.getValue() ? "green" : "gray"}>{info.getValue() ? "Actif" : "Inactif"}</Badge> }),
      col.display({
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <HStack>
            <IconButton aria-label="Modifier" size="sm" icon={<EditIcon />} onClick={() => openEdit(row.original)} />
            <IconButton aria-label="Supprimer" size="sm" icon={<DeleteIcon />} colorScheme="red" onClick={() => handleDeleteClick(row.original)} isLoading={deletingId === row.original.id} />
          </HStack>
        ),
      }),
    ];
  }, [openEdit, handleDeleteClick, deletingId]);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" alignItems="start" px={{ base: "20px", sm: "24px" }} minHeight="100vh">
          <Flex justifyContent="space-between" alignItems="center" w="100%" flexWrap="wrap" gap={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Bus</Heading>
            <Button size="xl" variant="outline" colorScheme="red_origin" onClick={openCreate}>Créer un bus</Button>
          </Flex>
          {error && <Box mt={4} p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement.</Text></Box>}
          <Box mt={4} bg="white" w="100%" borderRadius="10px">
            <Flex p={4}><InputGroup w={{ base: "100%", sm: "300px" }}>
              <Input placeholder="Rechercher" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
              <InputRightElement>{searchValue ? <IconButton aria-label="Effacer" size="sm" icon={<CloseIcon />} onClick={() => setSearchValue("")} /> : null}</InputRightElement>
            </InputGroup></Flex>
            {isLoading && !filteredData.length ? <Flex justify="center" py={10}><Spinner size="lg" /></Flex> : <ReactTable columns={columns as any} data={filteredData} size="sm" />}
            {!isLoading && !filteredData.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucun bus.</Text></Box>}
          </Box>
        </Flex>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{formMode === "create" ? "Ajouter un bus" : "Modifier le bus"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl isRequired><FormLabel>Nom</FormLabel><Input value={formValues.name} onChange={(e) => setFormValues((p) => ({ ...p, name: e.target.value }))} /></FormControl>
              <FormControl isRequired><FormLabel>Immatriculation</FormLabel><Input value={formValues.plate_number} onChange={(e) => setFormValues((p) => ({ ...p, plate_number: e.target.value }))} /></FormControl>
              <FormControl><FormLabel>ID conducteur (optionnel)</FormLabel><Input type="number" value={formValues.driver_id ?? ""} onChange={(e) => setFormValues((p) => ({ ...p, driver_id: e.target.value ? parseInt(e.target.value, 10) : null }))} /></FormControl>
              <FormControl><Flex align="center"><FormLabel mb={0}>Actif</FormLabel><Switch isChecked={formValues.is_active} onChange={(e) => setFormValues((p) => ({ ...p, is_active: e.target.checked }))} /></Flex></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>Enregistrer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Supprimer le bus</AlertDialogHeader>
            <AlertDialogBody>Êtes-vous sûr de vouloir supprimer ce bus ?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>Annuler</Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={deletingId === activeItem?.id} ml={3}>Supprimer</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
